import { useTranslation } from 'react-i18next';
import { weekdayLabel } from '../../lib/schedule/formatDaysOfWeek';

type DayOfWeekPickerProps = {
  value: number[];
  onChange: (days: number[]) => void;
  disabled?: boolean;
};

/** Display order Mon→Sun; contract values stay 0=Sun … 6=Sat. Empty = every day. */
const DISPLAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/**
 * Day chips matching Schedule.dc.html:
 * empty selection → summary “Every day”, no chip selected;
 * create/API still treat empty as every day.
 */
export function DayOfWeekPicker({ value, onChange, disabled }: DayOfWeekPickerProps) {
  const { t } = useTranslation('schedule');
  const summary =
    value.length === 0 || value.length === 7
      ? t('days.everyDay')
      : t('days.selected', { count: value.length });

  return (
    <span className="flex flex-col gap-[9px]">
      <span className="flex items-baseline gap-2.5">
        <span className="text-[12.5px] font-bold" style={{ color: 'var(--gs-ink2)' }}>
          {t('days.label')}
        </span>
        <span className="text-xs" style={{ color: 'var(--gs-muted)' }}>
          {summary}
        </span>
      </span>
      <span className="flex flex-wrap gap-1.5">
        {DISPLAY_ORDER.map((day) => {
          const active = value.includes(day);
          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              onClick={() => {
                if (value.includes(day)) {
                  onChange(value.filter((item) => item !== day));
                } else {
                  onChange([...value, day].sort((a, b) => a - b));
                }
              }}
              className="rounded-[9px] border px-3 py-2 text-[13px] font-semibold"
              style={{
                color: active ? 'var(--gs-ink)' : 'var(--gs-muted)',
                background: active ? 'var(--gs-panel2)' : 'transparent',
                borderColor: active ? 'var(--gs-accent)' : 'var(--gs-line)',
                fontFamily: 'inherit',
              }}
              aria-pressed={active}
            >
              {weekdayLabel(day)}
            </button>
          );
        })}
      </span>
    </span>
  );
}
